import React, { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";

import {
  emptyString,
  featureFlagsEnums,
  modalButtonText,
  modalSize,
  verifyMessages,
  saveFeedbackModalConstants,
} from "../../../../constants";
import { getConfirmationModal } from "../allModals";
import useModalDisplay from "../../hooks/useModalDisplay/useModalDisplay";

const APPLIED_TO_ALL_TRUE = true;

const UpdateFeatureFlagsModal = (props) => {
  const {
    show,
    onClose,
    animate,
    onConfirm,
    ModalSize,
    CancelButtonText,
    UpdateButtonText,
    SubmitButtonStyle,
    CancelButtonStyle,
    SubmitButtonVariant,
    SubmitButtonClasses,
    CancelButtonVariant,
    CancelButtonClasses,
    data: {
      brandId,
      brandName,
      isStoreUpdate = false,
      storeDetail = emptyString,
    },
  } = props;

  const features = isStoreUpdate
    ? props.data.storeFeatures
    : props.data.brandFeatures;

  const [selectedFeature, setSelectedFeature] = useState([]);
  const [isStatusUpdated, setIsStatusUpdated] = useState(false);

  const [showModal, modalElement, setComponent, closeModal] = useModalDisplay();

  const isInboxActivated = selectedFeature.includes(featureFlagsEnums.INBOX);
  const isAIReplyActivated = selectedFeature.includes(
    featureFlagsEnums.AI_REPLY
  );
  const isNpsFirstActivated = selectedFeature.includes(
    featureFlagsEnums.NPS_FIRST
  );

  const handleCheckboxChange = ({ target: { value } }) => {
    const numericValue = Number(value);
    setSelectedFeature((prev) => {
      const updatedFeatures = prev.includes(numericValue)
        ? prev.filter((feature) => feature !== numericValue)
        : [...prev, numericValue];

      // Check if the updated features match the original features
      const hasChanged =
        updatedFeatures?.length !== features?.length ||
        updatedFeatures.some((feature) => !features.includes(feature));
      setIsStatusUpdated(hasChanged);
      return updatedFeatures;
    });
  };

  const onUpdate = () => {
    let selectedFeaturesToUpdate = [...selectedFeature];
    if (!isInboxActivated && isAIReplyActivated) {
      selectedFeaturesToUpdate = selectedFeaturesToUpdate.filter(
        (f) => f !== featureFlagsEnums.AI_REPLY
      );
    }

    let onlyFeatureRemoved = false;

    const removedFeatures = features.filter(
      (f) => !selectedFeaturesToUpdate.includes(f)
    );

    const addedFeatures = selectedFeaturesToUpdate.filter(
      (f) => !features.includes(f)
    );

    const onlyInStoreAdded = addedFeatures.every(
      (feature) =>
        feature === featureFlagsEnums.IN_STORE && removedFeatures?.length === 0
    );

    onlyFeatureRemoved =
      removedFeatures.every((f) => features.includes(f)) &&
      addedFeatures?.length === 0;

    let dataToSend = {
      brandId,
      brandName,
      ...(!isStoreUpdate && { brandFeatures: selectedFeaturesToUpdate }),
      ...(isStoreUpdate && {
        storeId: storeDetail.storeId,
        storeName: storeDetail.storeName,
        storeFeatures: selectedFeaturesToUpdate,
      }),
    };

    if (isStoreUpdate) {
      onConfirm(dataToSend, isStoreUpdate);
    } else {
      if (onlyFeatureRemoved || onlyInStoreAdded) {
        dataToSend = {
          ...dataToSend,
          applyToAllStores: APPLIED_TO_ALL_TRUE,
        };
        onConfirm(dataToSend, isStoreUpdate);
      } else {
        setComponent(
          getConfirmationModal({
            onClose: () => {
              closeModal();
            },
            Title: saveFeedbackModalConstants.TITLE,
            isBrandFeatureUpdate: true,
            message: verifyMessages.APPLIED_TO_ALL,
            ModalSize: modalSize.MODAL_SIZE_MEDIUM,
            CancelButtonText: modalButtonText.NO,
            onConfirm: () => {
              dataToSend = {
                ...dataToSend,
                applyToAllStores: APPLIED_TO_ALL_TRUE,
              };
              onConfirm(dataToSend, isStoreUpdate);
            },
          })
        );
      }
    }
  };

  useEffect(() => {
    if (features?.length) {
      setSelectedFeature([...features]);
    }
  }, [features]);

  return (
    <Modal
      centered
      show={show}
      onHide={onClose}
      animation={animate}
      size={ModalSize}
      className="react-survey-modal"
    >
      {showModal && modalElement}
      <Modal.Header closeButton>
        <Modal.Title>
          {isStoreUpdate ? storeDetail.storeName : brandName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Inbox Feature */}
        <Form.Group className="row align-items-center mb-1 m-0">
          <Form.Label className="col-sm-3 col-form-label">Inbox:</Form.Label>
          <div className="col-sm-9">
            <Form.Check
              type="checkbox"
              name="inboxFeature"
              onChange={handleCheckboxChange}
              value={featureFlagsEnums.INBOX}
              data-testid="inbox-feature-check"
              checked={isInboxActivated}
            />
          </div>
        </Form.Group>
        {/* AI Reply Feature */}
        {isInboxActivated && (
          <Form.Group className="row align-items-center mb-1 m-0">
            <Form.Label className="col-sm-3 col-form-label">
              AI Reply:
            </Form.Label>
            <div className="col-sm-9">
              <Form.Check
                type="checkbox"
                name="inboxFeature"
                onChange={handleCheckboxChange}
                value={featureFlagsEnums.AI_REPLY}
                data-testid="ai-Reply-feature-check"
                checked={isAIReplyActivated}
              />
            </div>
          </Form.Group>
        )}

        {/* NPS-First Feature */}
        <Form.Group className="row align-items-center mb-1 m-0">
          <Form.Label className="col-sm-3 col-form-label">
            NPS-First:
          </Form.Label>
          <div className="col-sm-9">
            <Form.Check
              type="checkbox"
              name="npsFirstFeature"
              onChange={handleCheckboxChange}
              value={featureFlagsEnums.NPS_FIRST}
              data-testid="nps-first-feature-check"
              checked={isNpsFirstActivated}
            />
          </div>
        </Form.Group>

        {/* In-Store Feature */}
        {!isStoreUpdate && (
          <Form.Group className="row align-items-center mb-1 m-0">
            <Form.Label className="col-sm-3 col-form-label">
              In-Store:
            </Form.Label>
            <div className="col-sm-9">
              <Form.Check
                type="checkbox"
                name="inStoreFeature"
                onChange={handleCheckboxChange}
                value={featureFlagsEnums.IN_STORE}
                data-testid="in-store-feature-check"
                checked={selectedFeature.includes(featureFlagsEnums.IN_STORE)}
              />
            </div>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={SubmitButtonVariant}
          className={SubmitButtonClasses}
          style={SubmitButtonStyle}
          onClick={onUpdate}
          disabled={!isStatusUpdated}
        >
          {UpdateButtonText}
        </Button>
        <Button
          variant={CancelButtonVariant}
          className={CancelButtonClasses}
          style={CancelButtonStyle}
          onClick={onClose}
        >
          {CancelButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateFeatureFlagsModal;
